import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * VoiceMicButton — push-to-talk capture → Whisper transcript → optional
 * vitals/symptoms parser → callback. One reusable button used in the
 * doctor's vitals section and the reception triage form.
 *
 * Props:
 *   onTranscript(text)            — every transcript fires this first
 *   onParsed({vitals, chief_complaint, symptoms})
 *                                 — fires only when mode === 'vitals'
 *                                   after /voice/parse-vitals runs.
 *   mode:    'raw' | 'vitals'     — 'vitals' chains transcription with
 *                                   the structured parser. 'raw' just
 *                                   emits the transcript.
 *   label:   string               — accessibility label / tooltip
 *   size:    'sm' | 'md'
 *   className, data-testid
 */
export default function VoiceMicButton({
  onTranscript, onParsed, mode = 'vitals', label = 'Dictate', size = 'sm', className = '', ...rest
}) {
  const [state, setState] = useState('idle'); // idle | recording | processing
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mr.start();
      setState('recording');
    } catch (e) {
      toast.error('Microphone permission denied');
      setState('idle');
    }
  };

  const stop = () => {
    try {
      mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
    } catch (_) {}
  };

  const handleStop = async () => {
    setState('processing');
    try {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const fd = new FormData();
      fd.append('audio', blob, 'dictation.webm');
      const { data } = await ax.post('/api/transcribe', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = (data?.text || '').trim();
      if (!text) {
        toast.warning('Nothing was captured. Please try again.');
        setState('idle');
        return;
      }
      onTranscript?.(text);
      if (mode === 'vitals') {
        try {
          const { data: parsed } = await ax.post('/api/clinic-ops/voice/parse-vitals', { transcript: text });
          onParsed?.(parsed);
          const got = Object.keys(parsed?.vitals || {}).length;
          if (got || parsed?.chief_complaint) {
            toast.success(`Captured ${got} vital${got === 1 ? '' : 's'}${parsed?.chief_complaint ? ' + complaint' : ''}`);
          } else {
            toast.message('Heard you, but no vitals parsed. Transcript saved.');
          }
        } catch (_) {
          toast.warning('Transcript captured — parsing failed.');
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Transcription failed.';
      toast.error(msg);
    }
    setState('idle');
  };

  const sizeCls = size === 'md' ? 'h-9 px-3.5 text-sm' : 'h-8 px-3 text-xs';
  const isRec = state === 'recording';
  const isProc = state === 'processing';

  return (
    <button
      type="button"
      onClick={isRec ? stop : start}
      disabled={isProc}
      title={label}
      data-mic-btn="voice-mic-btn"
      className={`inline-flex items-center gap-1.5 ${sizeCls} font-semibold rounded-lg border transition-all
        ${isRec ? 'bg-red-50 border-red-300 text-red-700 animate-pulse'
                : isProc ? 'bg-zinc-50 border-zinc-200 text-zinc-500 cursor-wait'
                : 'bg-white border-zinc-200 text-zinc-700 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700'}
        ${className}`}
      data-testid={rest['data-testid'] || 'voice-mic-btn'}
    >
      {isProc ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
       : isRec ? <Square className="h-3.5 w-3.5 fill-current" />
       : <Mic className="h-3.5 w-3.5" />}
      <span>{isProc ? 'Transcribing…' : isRec ? 'Stop' : label}</span>
    </button>
  );
}
